import { Layout, Tour, TourProps } from 'antd'
import { sidebarToggle } from 'src/store/actions'
import { BulbFilled, BulbOutlined, CheckOutlined, CloseOutlined, CopyOutlined, DownOutlined, SearchOutlined, SendOutlined } from '@ant-design/icons'
import { Alert, Button, Flex, Input, Typography } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { BrainCircuit } from 'lucide-react'
import useTranslate from 'src/utils/useTranslate'
import { callOpenAI } from './AiAssistantHelper'
import { StyledButton } from 'src/styled_components/StyledButton'
import { aiAssistantSidebarToggle } from 'src/store/actions/sidebar'
import { useDispatch, useSelector } from 'react-redux'
import { Remarkable } from 'remarkable'
import { workOrdersRequest } from 'src/store/actions/workOrders'
import { modelsConfigListRequest } from 'src/store/actions/formulate'
import { fetchProjectListRequest } from 'src/store/actions/projects'
import { AI_ASSISTANT_QUERIES_COLLECTION, firestoreDb } from 'src/utils/firebase'
import { collection, doc, setDoc } from 'firebase/firestore'
import { StoreState } from 'src/store/configureStore'
import DOMPurify from 'dompurify'
import { sendUserQuerySlackMessage } from 'src/services/aiAssist'
import jwtManager from 'src/utils/jwtManager'
import { defaultHeaders } from 'src/constants'
import { Headers } from "src/services/interface";
import { isInternalEmail } from './convertHelper'

type MessageType = { role: string; content: any; node?: React.ReactNode }

const md = new Remarkable()

export const AI_ASSISTANT_SIDEBAR_WIDTH = 450

const AI_ASSIST_TOUR_VISIBLE_KEY = 'ai-assist-tour-visible'

export const slackMessageTemplate = (
  user_name: string,
  query: string,
  company: string,
  created_at: string,
  environment: string,
  is_feedback: boolean = false
) => {
  const headerText = is_feedback ? "💬 Need Support for this" : "📬 New User Query";

  return {
    blocks: [
      {
        type: "header",
        text: {
          type: "plain_text",
          text: headerText
        }
      },
      {
        type: "context",
        elements: [
          {
            type: "mrkdwn",
            text: `🔧 *Environment:* \`${environment.toUpperCase()}\``
          }
        ]
      },
      {
        type: "divider"
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*👤 User:*  \`${user_name}\``
        }
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*🏢 Company:*  \`${company}\``
        }
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*📝 Query:*\n\n> ${query}`
        }
      },
      {
        type: "divider"
      },
      {
        type: "context",
        elements: [
          {
            type: "mrkdwn",
            text: `*🕒 Created at:* ${created_at}`
          }
        ]
      }
    ]
  };
};

const AiAssistantWrapper = () => {
  const dispatch = useDispatch()
  const [t] = useTranslate()

  const open = useSelector((state: StoreState) => state.sidebar.isAiAssistantCollapsed)
  const { user_id, user_name, company, company_id, user_email } = useSelector<StoreState, any>((state: StoreState) => state?.login?.loginResponse ?? {})

  const iconRef = React.useRef<HTMLDivElement>(null)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const suggestionsRef = React.useRef<HTMLDivElement>(null)
  const textareaDivRef = React.useRef<HTMLDivElement>(null)
  const messagesRef = React.useRef<HTMLDivElement>(null)
  const isComposing = React.useRef<boolean>(false)

  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(true)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [openTour, setOpenTour] = React.useState<boolean>(() => {
    const isAiAssistTourVisible = localStorage.getItem(AI_ASSIST_TOUR_VISIBLE_KEY)

    if(isAiAssistTourVisible === 'false') return false

    if(isAiAssistTourVisible === 'true') return true


    localStorage.setItem(AI_ASSIST_TOUR_VISIBLE_KEY, 'true')
    return true

  })
  const [hasUserScrolledUp, setHasUserScrolledUp] = useState(false)
  const [messages, setMessages] = useState<MessageType[]>([])

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Necessary data to be brought in from API call
  useEffect(() => {
    dispatch(
      workOrdersRequest({
        partial: true,
        status: 'closed'
      })
    )
    dispatch(modelsConfigListRequest())
    dispatch(fetchProjectListRequest({ set: true }))
  }, [dispatch])

  const handleScroll = () => {
    if (messagesRef.current) {
      const { scrollTop, clientHeight, scrollHeight } = messagesRef.current
      if (scrollTop + clientHeight < scrollHeight) {
        setHasUserScrolledUp(true)
      } else {
        setHasUserScrolledUp(false)
      }
    }
  }

  useEffect(() => {
    if (messagesRef.current && !hasUserScrolledUp) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }, [messages, hasUserScrolledUp])

  const handleSend = async (input: string) => {
    if (!input.trim()) return
    if (loading) return

    setLoading(true)

    const userMessage = { role: 'user', content: input }
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)

    let assistantMessage: MessageType = { role: 'assistant', content: '' }
    setMessages([...updatedMessages, assistantMessage])

    // Strip node key from the messages
    const messagesWithoutNode = updatedMessages.map((msg) => {
      const { node, ...rest } = msg as MessageType
      return rest
    })

    await callOpenAI(messagesWithoutNode, (token, node) => {
      assistantMessage.content += token
      assistantMessage.node = node
      setMessages([...updatedMessages, { ...assistantMessage }])
    })

    setLoading(false)
    textareaRef?.current?.focus()
    setInput('')

    try {
      const queriesRef = doc(collection(firestoreDb, AI_ASSISTANT_QUERIES_COLLECTION))
      await setDoc(queriesRef, {
        user_id,
        user_name,
        company,
        company_id,
        query: input,
        created_at: new Date().toString(),
        environment: process.env.REACT_APP_ENV || 'dev'
      })
      const headers:Headers = { ...defaultHeaders, token: jwtManager.getToken() ?? '' }

      if(!isInternalEmail(user_email) && process.env.REACT_APP_ENV === 'production'){
        await sendUserQuerySlackMessage(slackMessageTemplate(user_name, input, company, new Date().toLocaleString(), process.env.REACT_APP_ENV || 'dev'), headers)
      }
    } catch (e) {
      console.log('Error while saving query to Firestore: ', e)
    }

    // Reset the suggestions
    setIsSuggestionsOpen(true)
  }

  const clearMessages = () => {
    setMessages([])
    setHasUserScrolledUp(false)
  }

  const suggestions = useMemo(
    () => [
      t('aiAssistant.suggestion1'),
      t('aiAssistant.suggestion2'),
      t('aiAssistant.suggestion3'),
      t('aiAssistant.suggestion4'),
      t('aiAssistant.suggestion5'),
      t('aiAssistant.suggestion6'),
      t('aiAssistant.suggestion7'),
      t('aiAssistant.suggestion8'),
      t('aiAssistant.suggestion9'),
      t('aiAssistant.suggestion10'),
      t('aiAssistant.suggestion11'),
      t('aiAssistant.suggestion12'),
      t('aiAssistant.suggestion13'),
      t('aiAssistant.suggestion14'),
      t('aiAssistant.suggestion15'),
      t('aiAssistant.suggestion16'),
      t('aiAssistant.suggestion17'),
      t('aiAssistant.suggestion18'),
      t('aiAssistant.suggestion19')
    ],
    [t]
  )

  const [tourStep, setTourStep] = useState<number>(0)

  const steps: TourProps['steps'] = [
    {
      title: t('aiAssistant.tour.title1'),
      description: t('aiAssistant.tour.description1'),
      target: iconRef.current,
      placement: 'topLeft',
      onNext: () => {
        openAiAssist()
        setTourStep((prev) => prev + 1)
      },
      prevButtonProps: {
        children: t('common.previous'),
        style: {
          outline: 'none'
        }
      },
      nextButtonProps: {
        children: t('common.next'),
        style: {
          outline: 'none'
        }
      }
    },
    {
      title: t('aiAssistant.tour.title2'),
      description: t('aiAssistant.tour.description2'),
      target: suggestionsRef.current,
      onPrev: () => {
        closeAiAssist()
        setTourStep((prev) => prev - 1)
      },
      onNext: () => {
        setTourStep((prev) => prev + 1)
      },
      prevButtonProps: {
        children: t('common.previous'),
        style: {
          outline: 'none'
        }
      },
      nextButtonProps: {
        children: t('common.next'),
        style: {
          outline: 'none'
        }
      }
    },
    {
      title: t('aiAssistant.tour.title3'),
      description: t('aiAssistant.tour.description3'),
      target: textareaDivRef.current,
      onPrev: () => {
        setTourStep((prev) => prev - 1)
      },
      prevButtonProps: {
        children: t('common.previous'),
        style: {
          outline: 'none'
        }
      },
      nextButtonProps: {
        children: t('common.finish'),
        style: {
          outline: 'none'
        }
      },
      onFinish: () => {
        localStorage.setItem(AI_ASSIST_TOUR_VISIBLE_KEY, 'false')
        setOpenTour(false)
      }
    }
  ]

  const openAiAssist = () => {
    dispatch(aiAssistantSidebarToggle(true))
    dispatch(sidebarToggle(true))
  }

  const closeAiAssist = () => {
    dispatch(aiAssistantSidebarToggle(false))
  }

  useEffect(() => {
    if (openTour) {
      if (open) setTourStep(1)
      else setTourStep(0)
    }
  }, [open, openTour])

  return (
    <>
      <div className="widget-container">
        <div
          className={`widget-open-button`}
          onClick={() => {
            openAiAssist()
          }}
          style={{
            bottom: '500px',
            background: 'radial-gradient(77.96% 77.96% at 87.21% 7.54%, #00E1FF 0%, #007BFF 100%)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            gap: '0.5rem',
            fontWeight: 700,
            opacity: !open ? 1 : 0
          }}
          ref={iconRef}
        >
          <BrainCircuit size={16} style={{ height: '100%', transform: 'rotate(90deg)', strokeWidth: 2.5 }} />
          {t('aiAssistant.title')}
        </div>
      </div>
      <Layout.Sider
        width={AI_ASSISTANT_SIDEBAR_WIDTH}
        className="styled-sider"
        theme="light"
        style={{
          display: open ? 'block' : 'none',
          height: '100%',
          zIndex: 999,
          background: 'white'
        }}
      >
        <Flex justify="space-between" align="center" style={{ padding: '1rem', height: '70px' }}>
          <Flex gap={'small'} align="center">
            <BrainCircuit size={16} style={{ height: '100%', color: '#1890ff' }} />
            <Typography.Title level={4} style={{ color: '#1890ff', display: 'flex', gap: '1rem' }}>
              {t('aiAssistant.title')}
            </Typography.Title>
          </Flex>

          <Flex gap={'small'} align="center">
            <StyledButton
              type="link"
              onClick={() => {
                clearMessages()
              }}
              disabled={messages.length === 0}
            >
              {t('common.Clear')}
            </StyledButton>
            <StyledButton
              type="link"
              onClick={() => {
                dispatch(aiAssistantSidebarToggle(false))
              }}
              icon={<CloseOutlined />}
              style={{ color: 'black' }}
            />
          </Flex>
        </Flex>

        <div
          style={{
            padding: '2rem',
            paddingTop: 0,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 70px)',
            justifyContent: 'space-between'
          }}
        >
          <Flex vertical gap="middle" style={{ width: '100%', overflow: 'auto', scrollbarWidth: 'none' }} ref={messagesRef} onScroll={handleScroll}>
            {messages.length === 0 && (
              <Alert
                message={
                  <Flex gap={'small'} align="center">
                    <BrainCircuit size={16} style={{ height: '100%', color: '#1890ff' }} />
                    <Typography.Title level={5}>{t('aiAssistant.title')}</Typography.Title>
                  </Flex>
                }
                description={t('aiAssistant.description')}
                type="info"
                showIcon={false}
                style={{ marginBottom: '1rem', border: 'none' }}
              />
            )}

            {messages.length > 0 &&
              messages.map((msg) => {
                return (
                  <React.Fragment key={msg.content}>
                    {msg.role === 'assistant' ? (
                      <Typography.Text
                        style={{
                          fontWeight: '600',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem'
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(md.render(msg.content)) }} />
                        {msg.node}
                      </Typography.Text>
                    ) : (
                      <Flex
                        gap={'small'}
                        style={{
                          alignSelf: 'flex-end',
                          maxWidth: '80%'
                        }}
                        vertical
                        align="end"
                      >
                        <Typography.Text
                          style={{
                            fontWeight: '600',
                            width: '100%',
                            background: '#E4F2FF',
                            padding: '0.5rem',
                            borderRadius: '0.5rem',
                            wordWrap: 'break-word',
                            borderBottomRightRadius: '0',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {msg.content}
                        </Typography.Text>
                        <CopyComponent content={msg.content} />
                      </Flex>
                    )}
                  </React.Fragment>
                )
              })}

            {loading && (
              <Typography.Text
                style={{
                  fontWeight: '600',
                  gap: '0.5rem'
                }}
                className="loading-text-blink"
              >
                {t('aiAssistant.thinking')}
              </Typography.Text>
            )}
          </Flex>

          <Flex vertical gap="middle" style={{ width: '100%', position: 'relative' }}>
            <StyledButton
              icon={<DownOutlined />}
              style={{
                position: 'absolute',
                top: '0',
                left: '50%',
                display: hasUserScrolledUp ? 'block' : 'none',
                transform: 'translate(-50%, -50%)'
              }}
              onClick={() => {
                if (messagesRef.current) {
                  messagesRef.current.scrollTop = messagesRef.current.scrollHeight
                }
                setHasUserScrolledUp(false)
              }}
            />

            {isSuggestionsOpen && (
              <Alert
                type="info"
                style={{
                  border: 'none',
                  padding: 0,
                  marginTop: '2rem',
                  backgroundColor: 'transparent'
                }}
                description={
                  <Flex
                    gap={8}
                    wrap="wrap"
                    style={{
                      height: 125,
                      overflow: 'scroll'
                    }}
                    ref={suggestionsRef}
                  >
                    {suggestions.map((sug, idx) => {
                      return (
                        <Button
                          icon={<SearchOutlined />}
                          type="default"
                          shape="round"
                          style={{
                            backgroundColor: '#e6f4ff',
                            border: 'none',
                            outline: 'none'
                          }}
                          key={idx}
                          onClick={() => {
                            handleSend(sug)
                            setIsSuggestionsOpen(false)
                          }}
                        >
                          {sug}
                        </Button>
                      )
                    })}
                  </Flex>
                }
              />
            )}

            <div
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '0.5rem',
                boxShadow: '0 0 10px 0 rgba(0,0,0,0.1)',
                border: '1px solid #d9d9d9',
                background: 'white',
                display: 'flex',
                gap: '0.75rem',
                marginTop: isSuggestionsOpen ? 0 : '2rem',
                alignItems: 'start'
              }}
              ref={textareaDivRef}
            >
              <Input.TextArea
                placeholder={t('aiAssistant.placeholder')}
                onPressEnter={(e) => {
                  if (e.shiftKey) {
                    return
                  }
                  if (!isComposing.current) {
                    e.preventDefault()
                    handleSend(input)
                  }
                }}
                onCompositionStart={() => {
                  isComposing.current = true
                }}
                onCompositionEnd={() => {
                  isComposing.current = false
                }}
                size="large"
                style={{
                  width: '100%',
                  background: 'white',
                  boxShadow: 'none',
                  border: 'none',
                  outline: 'none',
                  flexGrow: 1,
                  padding: 0,
                  resize: 'none'
                }}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value)
                }}
                disabled={loading || !isOnline}
                styles={{
                  textarea: {
                    boxShadow: '0 0 10px 0 rgba(0,0,0,0.1)'
                  }
                }}
                autoFocus
                ref={textareaRef}
                draggable={false}
              />
              <StyledButton
                type="link"
                onClick={() => {
                  setIsSuggestionsOpen((prev) => !prev)
                }}
                shape="circle"
                icon={isSuggestionsOpen ? <BulbFilled style={{ color: '#ffbe00' }} /> : <BulbOutlined style={{ color: '#ffbe00' }} />}
              />
              <StyledButton
                type="primary"
                onClick={() => handleSend(input)}
                loading={loading}
                disabled={!isOnline}
                shape="circle"
                icon={<SendOutlined />}
              />
            </div>
          </Flex>
        </div>
      </Layout.Sider>

      <Tour
        open={openTour}
        onClose={() => {
          setOpenTour(false)
        }}
        onFinish={() => {
          localStorage.setItem(AI_ASSIST_TOUR_VISIBLE_KEY, 'false')
          setOpenTour(false)
        }}
        type="default"
        steps={steps}
        current={tourStep}
        animated
        gap={{
          offset: 15,
          radius: 10
        }}
      />
    </>
  )
}

export default AiAssistantWrapper

const CopyComponent = ({ content }: { content: string }) => {
  const [clicked, setClicked] = useState(false)

  useEffect(() => {
    if (clicked) {
      const timer = setTimeout(() => {
        setClicked(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [clicked])

  return clicked ? (
    <CheckOutlined
      style={{
        outline: 'none',
        color: '#52c41a'
      }}
    />
  ) : (
    <CopyOutlined
      style={{
        cursor: 'pointer',
        outline: 'none',
        color: '#1890ff'
      }}
      onClick={() => {
        setClicked(true)
        navigator.clipboard.writeText(content)
      }}
    />
  )
}
